// components/venues/VendorForm.js
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "./VendorForm.css";

const VendorForm = () => {
  const API_URL = process.env.REACT_APP_API_URL;
  const uniqueCode = sessionStorage.getItem("uniqueCode");
 const user = JSON.parse(sessionStorage.getItem("user"));
const role = user?.role;   

  const [formData, setFormData] = useState({
    name: "",
    uploadedBy: uniqueCode,
    location: "",
    address: "",   // ✅ added
    rating: "",
    reviews: "",
    description: "",
    price: "",
    promotion: "",
    images: [],
    category: "Vendors",
    subcategory: "",
    services: [],
    email: "",
    openServices: false,
    video: null,
  });

  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState(false);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [videoPreview, setVideoPreview] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);

  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchCategories();
    fetchVendors();
  }, []);

  // ✅ Fetch categories for subcategory dropdown
  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/categories`);
      const vendorSubcategories = res.data
        .filter((cat) => cat.category === "Vendors")
        .flatMap((cat) =>
          Array.isArray(cat.subcategory) ? cat.subcategory : []
        );
      const normalized = res.data.map((cat) => ({
        ...cat,
        subcategory: Array.isArray(cat.subcategory)
          ? cat.subcategory
          : cat.subcategory
          ? [cat.subcategory]
          : [],
        services: vendorSubcategories,
      }));
      setCategories(normalized);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  // ✅ Role-based vendor filtering
  const fetchVendors = async () => {
    try {
      const res = await axios.get(`${API_URL}/catagory`);
      const allVendors = res.data.filter(
        (item) => item.category?.toLowerCase() === "vendors"
      );

      if (role === "admin") {
        setVendors(allVendors);
      } else if (uniqueCode) {
        setVendors(allVendors.filter((v) => v.uploadedBy === uniqueCode));
      } else {
        setVendors([]);
      }
    } catch (err) {
      console.error("Error fetching vendors:", err);
    }
  };

  // ✅ Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ✅ Handle multiple image uploads
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const newImagePreviews = files.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
      }));
      setImagePreviews((prev) => [...prev, ...newImagePreviews]);
      setFormData((prev) => ({
        ...prev,
        images: [...prev.images, ...files],
      }));
    }
  };

  // ✅ Handle video upload
  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setVideoPreview(URL.createObjectURL(file));
      setFormData((prev) => ({ ...prev, video: file }));
    }
  };

  // ✅ Remove image
  const removeImage = (index) => {
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  // ✅ Remove video
  const removeVideo = () => {
    setVideoPreview(null);
    setFormData((prev) => ({ ...prev, video: null }));
  };

  // ✅ Edit vendor
  const handleEdit = (vendor) => {
    setFormData({
      ...vendor,
      uploadedBy: uniqueCode,
      services: Array.isArray(vendor.services)
        ? vendor.services
        : vendor.services
        ? [vendor.services]
        : [],
      openServices: false,
      images: [],
      video: null,
    });

    if (Array.isArray(vendor.images)) {
      setImagePreviews(
        vendor.images.map((img) => ({
          file: null,
          preview: `${API_URL}/${img}`,
          fromDB: true,
        }))
      );
    } else if (vendor.image) {
      setImagePreviews([
        {
          file: null,
          preview: `${API_URL}/${vendor.image}`,
          fromDB: true,
        },
      ]);
    } else {
      setImagePreviews([]);
    }

    if (vendor.video) {
      setVideoPreview(`${API_URL}/${vendor.video}`);
    }

    setEditingId(vendor._id);
  };

  // ✅ Delete vendor
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this vendor?")) return;
    try {
      await axios.delete(`${API_URL}/catagory/${id}`);
      setVendors((prev) => prev.filter((item) => item._id !== id));
      setMessage("Vendor deleted successfully!");
      setError(false);
    } catch (err) {
      console.error("Error deleting vendor:", err);
      setMessage("Failed to delete. Please try again.");
      setError(true);
    }
  };

  // ✅ Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach((key) => {
        if (key === "services") {
          formData[key].forEach((service) =>
            formDataToSend.append("services", service)
          );
        } else if (key === "images") {
          formData.images.forEach((img) => {
            if (img) formDataToSend.append("images", img);
          });
        } else if (key === "video" && formData.video) {
          formDataToSend.append("video", formData.video);
        } else if (key !== "openServices" && formData[key] !== null) {
          formDataToSend.append(key, formData[key]);
        }
      });

      if (editingId) {
        await axios.put(`${API_URL}/catagory/${editingId}`, formDataToSend, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setMessage("Vendor updated successfully!");
      } else {
        await axios.post(`${API_URL}/catagory`, formDataToSend, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setMessage("Vendor added successfully!");
      }

      setError(false);
      resetForm();
      fetchVendors();
    } catch (err) {
      console.error("Save error:", err.response?.data || err.message);
      setMessage(
        "Error saving vendor: " + (err.response?.data?.error || err.message)
      );
      setError(true);
    }
  };

  // ✅ Reset form
  const resetForm = () => {
    setFormData({
      name: "",
      uploadedBy: uniqueCode,
      location: "",
      address: "",
      rating: "",
      reviews: "",
      description: "",
      price: "",
      promotion: "",
      images: [],
      category: "Vendors",
      subcategory: "",
      services: [],
      email: "",
      openServices: false,
      video: null,
    });
    setEditingId(null);
    setImagePreviews([]);
    setVideoPreview(null);
  };

  const handleView = (vendor) => setSelectedVendor(vendor);

  return (
    <div className="vendor-form-container">
      <h2>{editingId ? "Edit Vendor" : "Add New Vendor"}</h2>

      {message && (
        <p className={`vendor-form-message ${error ? "error" : "success"}`}>
          {message}
        </p>
      )}

      <form onSubmit={handleSubmit} className="vendor-form">
        <input
          type="text"
          name="name"
          placeholder="Name"
          value={formData.name}
          onChange={handleChange}
          required
        />

        {/* ✅ Uploaded By field */}
        <div className="uploaded-by-field">
          <label>Uploaded By (Unique Code):</label>
          <input
            type="text"
            name="uploadedBy"
            value={formData.uploadedBy}
            readOnly
            style={{ background: "#f5f5f5", cursor: "not-allowed" }}
          />
        </div>

        <input
          type="text"
          name="location"
          placeholder="Location"
          value={formData.location}
          onChange={handleChange}
          required
        />

        {/* ✅ Address field */}
        <input
          type="text"
          name="address"
          placeholder="Address"
          value={formData.address}
          onChange={handleChange}
          required
        />

        <input
          type="number"
          name="rating"
          placeholder="Rating"
          value={formData.rating}
          onChange={handleChange}
          min="0"
          max="5"
          step="0.1"
        />

        <input
          type="number"
          name="price"
          placeholder="Price"
          value={formData.price}
          onChange={handleChange}
          min="0"
          required
        />

        <input
          type="email"
          name="email"
          placeholder="Email"
          value={formData.email}
          onChange={handleChange}
          required
        />

        <textarea
          name="description"
          placeholder="Description"
          value={formData.description}
          onChange={handleChange}
        ></textarea>

        {/* ✅ Subcategory dropdown only for Admin */}
        {role === "admin" && (
          <div className="subcategory-field">
            <label>Subcategory:</label>
            <select
              name="subcategory"
              value={formData.subcategory}
              onChange={handleChange}
            >
              <option value="">-- Select Subcategory --</option>
              {categories
                .filter((cat) => cat.category === "Vendors")
                .flatMap((cat) => cat.subcategory)
                .map((sub, idx) => (
                  <option key={idx} value={sub}>
                    {sub}
                  </option>
                ))}
            </select>
          </div>
        )}

        {/* Image Upload */}
        <div className="image-upload-section">
          <label>Images (Multiple):</label>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageChange}
          />
          {imagePreviews.length > 0 && (
            <div className="image-previews">
              {imagePreviews.map((preview, idx) => (
                <div key={idx} className="image-preview-item">
                  <img src={preview.preview} alt="Preview" />
                  <button
                    type="button"
                    className="remove-image-btn"
                    onClick={() => removeImage(idx)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Video Upload */}
        <div className="video-upload-section">
          <label>Video:</label>
          <input type="file" accept="video/*" onChange={handleVideoChange} />
          {videoPreview && (
            <div className="video-preview">
              <video controls>
                <source src={videoPreview} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <button
                type="button"
                className="remove-video-btn"
                onClick={removeVideo}
              >
                Remove Video
              </button>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <button type="submit">
          {editingId ? "Update Vendor" : "Add Vendor"}
        </button>
        {editingId && (
          <button type="button" onClick={resetForm}>
            Cancel Edit
          </button>
        )}
      </form>

      {/* Vendor Table */}
      <h3>All Vendors</h3>
      <table className="vendor-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Uploaded By</th>
            <th>Category</th>
            <th>Subcategory</th>
            <th>Services</th>
            <th>Location</th>
            <th>Address</th>
            <th>Price</th>
            <th>Email</th>
            <th>Rating</th>
            <th>Image</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {vendors.map((vendor) => (
            <tr key={vendor._id}>
              <td>{vendor.name}</td>
              <td>{vendor.uploadedBy || "N/A"}</td>
              <td>{vendor.category}</td>
              <td>{vendor.subcategory}</td>
              <td>
                {Array.isArray(vendor.services)
                  ? vendor.services.join(", ")
                  : vendor.services}
              </td>
              <td>{vendor.location}</td>
              <td>{vendor.address || "N/A"}</td>
              <td>₹{vendor.price || "N/A"}</td>
              <td>{vendor.email || "N/A"}</td>
              <td>
                {vendor.rating} ({vendor.reviews || 0} reviews)
              </td>
              <td>
                {Array.isArray(vendor.images) && vendor.images.length > 0 ? (
                  <img
                    src={`${API_URL}/${vendor.images[0]}`}
                    alt={vendor.name}
                    className="vendor-table-img"
                    onClick={() => {
                      setSelectedVendor(vendor);
                      setShowLightbox(true);
                    }}
                  />
                ) : vendor.image ? (
                  <img
                    src={`${API_URL}/${vendor.image}`}
                    alt={vendor.name}
                    className="vendor-table-img"
                  />
                ) : (
                  <div className="no-image">No Image</div>
                )}
              </td>
              <td>
                <button onClick={() => handleView(vendor)}>View</button>
                <button onClick={() => handleEdit(vendor)}>Edit</button>
                <button onClick={() => handleDelete(vendor._id)}>
                  Delete
                </button>
              </td>
            </tr>
          ))}
          {vendors.length === 0 && (
            <tr>
              <td colSpan="11" style={{ textAlign: "center" }}>
                No vendors found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default VendorForm;
